import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSurvey } from "../context/SurveyContext";
import { useAuth } from "../context/AuthContext";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Area,
  AreaChart,
  Legend,
} from "recharts";
import {
  Users,
  Clock,
  TrendingUp,
  FileText,
  Calendar,
  Download,
  RefreshCw,
  BarChart3,
  PieChartIcon,
  Activity,
  ArrowLeft,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Button } from "./ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Badge } from "./ui/badge";
import { Progress } from "./ui/progress";
import { DataTable } from "./ui/data-table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Label } from "./ui/label";
import { ColumnDef } from "@tanstack/react-table";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "./ui/chart";
import * as XLSX from "xlsx";
import "./SurveyAnalytics.css";
import { getBackendUrl } from "../config/env";

const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#8884D8",
  "#82CA9D",
];

interface AnalyticsData {
  survey: any;
  responses: any[];
  completionRate: number;
  averageCompletionTime: string;
  timelineData: any[];
  questionAnalytics: any[];
}

const SurveyAnalytics: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getSurvey } = useSurvey();
  const { user, centerInfo, selectedCenterId } = useAuth();

  const [analyticsData, setAnalyticsData] = useState<AnalyticsData>({
    survey: null,
    responses: [],
    completionRate: 0,
    averageCompletionTime: "0분",
    timelineData: [],
    questionAnalytics: [],
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [totalResponses, setTotalResponses] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pagedResponses, setPagedResponses] = useState<any[]>([]);

  useEffect(() => {
    loadAnalyticsData();
  }, [id, selectedCenterId]);

  useEffect(() => {
    if (activeTab === "responses" && id) {
      loadResponsesPage(currentPage, pageSize);
    }
  }, [activeTab, currentPage, pageSize, id]);

  const loadResponsesPage = async (page: number, size: number) => {
    try {
      const centerUserId =
        selectedCenterId || user?.centerId || centerInfo?.centerId;
      const params = new URLSearchParams({
        page: page.toString(),
        limit: size.toString(),
      });
      if (centerUserId) params.append("centerUserId", centerUserId);
      
      const responseUrl = `${getBackendUrl()}/api/responses/survey/${id}?${params.toString()}`;
      const responseData = await fetch(responseUrl);
      
      if (responseData.ok) {
        const data = await responseData.json();
        if (data.data && Array.isArray(data.data)) {
          setPagedResponses(data.data);
          setTotalResponses(data.total || 0);
          setTotalPages(data.totalPages || 1);
          setCurrentPage(data.page || 1);
        }
      }
    } catch (error) {
      console.error("Failed to load responses page:", error);
    }
  };

  const loadAnalyticsData = async () => {
    setLoading(true);
    try {
      // Load survey data
      const surveyResponse = await fetch(
        `${getBackendUrl()}/api/surveys/${id}`
      );
      let survey = null;
      if (surveyResponse.ok) {
        survey = await surveyResponse.json();
      } else {
        survey = getSurvey(id!);
      }

      // Load response data (get all responses for analytics)
      const centerUserId =
        selectedCenterId || user?.centerId || centerInfo?.centerId;
      const responseUrl = centerUserId
        ? `${getBackendUrl()}/api/responses/survey/${id}?centerUserId=${centerUserId}&limit=1000`
        : `${getBackendUrl()}/api/responses/survey/${id}?limit=1000`;

      const responseData = await fetch(responseUrl);
      let responses: any[] = [];
      if (responseData.ok) {
        const data = await responseData.json();
        // Handle paginated response format
        if (data.data && Array.isArray(data.data)) {
          responses = data.data;
        } else if (Array.isArray(data)) {
          responses = data;
        }
      }

      // Calculate metrics
      const completed = responses.filter(
        (r) => r.status === "completed"
      ).length;
      const completionRate =
        responses.length > 0
          ? Math.round((completed / responses.length) * 100)
          : 0;

      // Calculate average completion time
      const completedResponses = responses.filter(
        (r) => r.completedAt && r.startedAt
      );
      let averageCompletionTime = "0분";
      if (completedResponses.length > 0) {
        const totalTime = completedResponses.reduce((sum, r) => {
          const start = new Date(r.startedAt).getTime();
          const end = new Date(r.completedAt).getTime();
          return sum + (end - start);
        }, 0);
        const avgTime = totalTime / completedResponses.length;
        const minutes = Math.floor(avgTime / 60000);
        averageCompletionTime = `${minutes}분`;
      }

      // Generate timeline data
      const timelineData = generateTimelineData(responses);

      // Generate question analytics
      const questionAnalytics = generateQuestionAnalytics(survey, responses);

      setAnalyticsData({
        survey,
        responses,
        completionRate,
        averageCompletionTime,
        timelineData,
        questionAnalytics,
      });
    } catch (error) {
      console.error("Error loading analytics data:", error);
    } finally {
      setLoading(false);
    }
  };

  const generateTimelineData = (responses: any[]) => {
    const dateMap = new Map<string, { date: string; count: number }>();

    responses.forEach((response) => {
      const date = new Date(
        response.completedAt || response.startedAt || response.createdAt
      );
      const dateKey = date.toLocaleDateString("ko-KR");

      if (dateMap.has(dateKey)) {
        dateMap.get(dateKey)!.count++;
      } else {
        dateMap.set(dateKey, { date: dateKey, count: 1 });
      }
    });

    return Array.from(dateMap.values()).sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );
  };

  const generateQuestionAnalytics = (survey: any, responses: any[]) => {
    if (!survey || !survey.questions) return [];

    return survey.questions.map((question: any) => {
      const questionResponses = responses
        .map((r) => {
          const answer = r.answers?.find(
            (a: any) => a.questionId === question.id
          );
          return answer?.value;
        })
        .filter((v) => v !== undefined);

      if (question.type === "multiple-choice" || question.type === "checkbox") {
        const optionCounts = new Map<string, number>();
        question.options?.forEach((opt: string) => optionCounts.set(opt, 0));

        questionResponses.forEach((answer) => {
          if (Array.isArray(answer)) {
            answer.forEach((opt) => {
              optionCounts.set(opt, (optionCounts.get(opt) || 0) + 1);
            });
          } else if (answer) {
            optionCounts.set(answer, (optionCounts.get(answer) || 0) + 1);
          }
        });

        return {
          question: question.question,
          type: question.type,
          data: Array.from(optionCounts.entries()).map(([option, count]) => ({
            option,
            count,
            percentage:
              questionResponses.length > 0
                ? Math.round((count / questionResponses.length) * 100)
                : 0,
          })),
        };
      } else if (question.type === "rating") {
        const ratingCounts = new Map<number, number>();
        for (
          let i = question.minRating || 1;
          i <= (question.maxRating || 5);
          i++
        ) {
          ratingCounts.set(i, 0);
        }

        questionResponses.forEach((rating) => {
          if (typeof rating === "number") {
            ratingCounts.set(rating, (ratingCounts.get(rating) || 0) + 1);
          }
        });

        const average =
          questionResponses.length > 0
            ? questionResponses.reduce((sum, r) => sum + (r || 0), 0) /
              questionResponses.length
            : 0;

        return {
          question: question.question,
          type: question.type,
          average: average.toFixed(1),
          data: Array.from(ratingCounts.entries()).map(([rating, count]) => ({
            rating: `${rating}점`,
            count,
            percentage:
              questionResponses.length > 0
                ? Math.round((count / questionResponses.length) * 100)
                : 0,
          })),
        };
      } else {
        return {
          question: question.question,
          type: question.type,
          responseCount: questionResponses.length,
          samples: questionResponses.slice(0, 5),
        };
      }
    });
  };

  const exportData = () => {
    const { survey, responses } = analyticsData;
    if (!survey || responses.length === 0) {
      alert("내보낼 데이터가 없습니다.");
      return;
    }

    // Prepare data for Excel
    const worksheetData: any[][] = [];

    // Add headers
    const headers = ["응답자", "상태", "시작시간", "완료시간"];
    survey.questions?.forEach((q: any) => {
      headers.push(q.question);
    });
    worksheetData.push(headers);

    // Add data rows
    responses.forEach((response) => {
      const row = [
        response.respondentId,
        response.status === "completed" ? "완료" : "진행중",
        response.startedAt
          ? new Date(response.startedAt).toLocaleString("ko-KR")
          : "",
        response.completedAt
          ? new Date(response.completedAt).toLocaleString("ko-KR")
          : "",
      ];

      survey.questions?.forEach((q: any) => {
        const answer = response.answers?.find(
          (a: any) => a.questionId === q.id
        );
        if (q.type === "multiple-choice" && answer?.value) {
          // For multiple choice, show the selected option text
          const option = q.options?.find((opt: any) => opt.id === answer.value);
          row.push(option?.text || answer.value);
        } else {
          row.push(answer?.value || "");
        }
      });

      worksheetData.push(row);
    });

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(worksheetData);

    // Auto-size columns
    const maxWidth = 50;
    const cols = headers.map((header, i) => {
      const columnData = worksheetData.map((row) => row[i]?.toString() || "");
      const maxLength = Math.max(
        header.length,
        ...columnData.map((val) => val.length)
      );
      return { wch: Math.min(maxLength + 2, maxWidth) };
    });
    ws["!cols"] = cols;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, "응답 데이터");

    // Create summary sheet
    const summaryData = [
      ["설문조사 분석 요약"],
      [],
      ["설문 제목", survey.title],
      ["설명", survey.description || ""],
      ["총 응답 수", responses.length],
      [
        "완료된 응답",
        responses.filter((r: any) => r.status === "completed").length,
      ],
      ["완료율", `${analyticsData.completionRate}%`],
      ["평균 완료 시간", analyticsData.averageCompletionTime],
      [],
      ["질문별 분석"],
    ];

    // Add question analytics to summary
    analyticsData.questionAnalytics.forEach((qa: any) => {
      summaryData.push([]);
      summaryData.push([qa.question]);
      if (qa.type === "multiple-choice" || qa.type === "rating") {
        summaryData.push(["평균", qa.average || "-"]);
        qa.data?.forEach((item: any) => {
          summaryData.push([
            item.rating || item.option,
            `${item.count}개 (${item.percentage}%)`,
          ]);
        });
      } else {
        summaryData.push(["응답 수", qa.responseCount]);
      }
    });

    const ws2 = XLSX.utils.aoa_to_sheet(summaryData);
    ws2["!cols"] = [{ wch: 30 }, { wch: 40 }];
    XLSX.utils.book_append_sheet(wb, ws2, "분석 요약");

    // Save file
    XLSX.writeFile(
      wb,
      `analytics_${survey.title}_${new Date().toISOString().split("T")[0]}.xlsx`
    );
  };

  // Define columns for response table
  const responseColumns: ColumnDef<any>[] = useMemo(
    () => [
      {
        accessorKey: "respondentId",
        header: "응답자",
        cell: ({ row }) => (
          <div className="font-medium">{row.getValue("respondentId")}</div>
        ),
      },
      {
        accessorKey: "status",
        header: "상태",
        cell: ({ row }) => {
          const status = row.getValue("status") as string;
          return (
            <Badge variant={status === "completed" ? "default" : "secondary"}>
              {status === "completed" ? "완료" : "진행중"}
            </Badge>
          );
        },
      },
      {
        accessorKey: "completedAt",
        header: "완료일시",
        cell: ({ row }) => {
          const date = row.getValue("completedAt");
          return date ? new Date(date as string).toLocaleString("ko-KR") : "-";
        },
      },
      {
        id: "completionTime",
        header: "소요시간",
        cell: ({ row }) => {
          const response = row.original;
          if (response.startedAt && response.completedAt) {
            const minutes = Math.floor(
              (new Date(response.completedAt).getTime() -
                new Date(response.startedAt).getTime()) /
                60000
            );
            return `${minutes}분`;
          }
          return "-";
        },
      },
    ],
    []
  );

  if (loading) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const {
    survey,
    responses,
    completionRate,
    averageCompletionTime,
    timelineData,
    questionAnalytics,
  } = analyticsData;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            뒤로
          </Button>
          <div>
            <h1 className="text-3xl font-bold">{survey?.title} - 분석</h1>
            <p className="text-muted-foreground">설문조사 응답 데이터 분석</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadAnalyticsData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            새로고침
          </Button>
          <Button onClick={exportData}>
            <Download className="h-4 w-4 mr-2" />
            내보내기
          </Button>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">총 응답</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{responses.length}</div>
            <p className="text-xs text-muted-foreground">
              {responses.filter((r) => r.status === "completed").length} 완료
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">완료율</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{completionRate}%</div>
            <Progress value={completionRate} className="mt-2" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">평균 소요시간</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{averageCompletionTime}</div>
            <p className="text-xs text-muted-foreground">완료 기준</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">질문 수</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {survey?.questions?.length || 0}
            </div>
            <p className="text-xs text-muted-foreground">총 질문</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">
            <BarChart3 className="h-4 w-4 mr-2" />
            개요
          </TabsTrigger>
          <TabsTrigger value="questions">
            <PieChartIcon className="h-4 w-4 mr-2" />
            질문별 분석
          </TabsTrigger>
          <TabsTrigger value="timeline">
            <Activity className="h-4 w-4 mr-2" />
            시간대별
          </TabsTrigger>
          <TabsTrigger value="responses">
            <Users className="h-4 w-4 mr-2" />
            응답 목록
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>응답 상태 분포</CardTitle>
              <CardDescription>설문조사 응답 상태별 통계</CardDescription>
            </CardHeader>
            <CardContent>
              <ChartContainer
                config={{
                  completed: { label: "완료", color: "hsl(var(--chart-1))" },
                  inProgress: { label: "진행중", color: "hsl(var(--chart-2))" },
                }}
                className="h-[300px]"
              >
                <PieChart>
                  <Pie
                    data={[
                      {
                        name: "완료",
                        value: responses.filter((r) => r.status === "completed")
                          .length,
                      },
                      {
                        name: "진행중",
                        value: responses.filter((r) => r.status !== "completed")
                          .length,
                      },
                    ]}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ percent }) =>
                      `${((percent ?? 0) * 100).toFixed(0)}%`
                    }
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {[0, 1].map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <ChartTooltip content={<ChartTooltipContent />} />
                </PieChart>
              </ChartContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="questions" className="space-y-4">
          {questionAnalytics.map((qa, index) => (
            <Card key={index}>
              <CardHeader>
                <CardTitle className="text-lg">{qa.question}</CardTitle>
                <CardDescription>
                  {qa.type === "rating" &&
                    qa.average &&
                    `평균 평점: ${qa.average}`}
                  {qa.type === "text" && `${qa.responseCount}개 응답`}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {(qa.type === "multiple-choice" ||
                  qa.type === "checkbox" ||
                  qa.type === "rating") &&
                qa.data ? (
                  <ChartContainer
                    config={{
                      value: { label: "응답 수", color: "hsl(var(--chart-1))" },
                    }}
                    className="h-[200px]"
                  >
                    <BarChart data={qa.data}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey={qa.type === "rating" ? "rating" : "option"}
                      />
                      <YAxis />
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <Bar dataKey="count" fill="#8884d8">
                        {qa.data.map((entry: any, index: number) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ChartContainer>
                ) : (
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">응답 샘플:</p>
                    {qa.samples?.map((sample: string, idx: number) => (
                      <div key={idx} className="p-2 bg-muted rounded">
                        {sample}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="timeline" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>시간대별 응답 추이</CardTitle>
              <CardDescription>날짜별 응답 수 변화</CardDescription>
            </CardHeader>
            <CardContent>
              <ChartContainer
                config={{
                  count: { label: "응답 수", color: "hsl(var(--chart-1))" },
                }}
                className="h-[300px]"
              >
                <AreaChart data={timelineData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke="#8884d8"
                    fill="#8884d8"
                    fillOpacity={0.6}
                  />
                </AreaChart>
              </ChartContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="responses" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>응답 목록</CardTitle>
              <CardDescription>
                전체 {totalResponses}개의 응답 (페이지 {currentPage}/{totalPages})
              </CardDescription>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={responseColumns}
                data={pagedResponses}
                searchKey=""
                showPagination={false}
                showColumnToggle={true}
                pageSize={1000}
              />
              
              {/* 커스텀 페이지네이션 */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <div className="flex items-center gap-4">
                  <div className="text-sm text-muted-foreground">
                    {totalResponses > 0 ? (
                      <>
                        {(currentPage - 1) * pageSize + 1} -{" "}
                        {Math.min(currentPage * pageSize, totalResponses)} /{" "}
                        {totalResponses}개 표시
                      </>
                    ) : (
                      <>데이터 없음</>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm">페이지당</Label>
                    <Select
                      value={pageSize.toString()}
                      onValueChange={(value) => {
                        setPageSize(parseInt(value));
                        setCurrentPage(1);
                      }}
                    >
                      <SelectTrigger className="w-20">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="5">5개</SelectItem>
                        <SelectItem value="10">10개</SelectItem>
                        <SelectItem value="20">20개</SelectItem>
                        <SelectItem value="50">50개</SelectItem>
                        <SelectItem value="100">100개</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {totalPages > 1 && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                    >
                      처음
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      이전
                    </Button>
                    
                    {/* 페이지 번호 버튼들 */}
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum: number;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        if (pageNum < 1 || pageNum > totalPages) return null;
                        
                        return (
                          <Button
                            key={pageNum}
                            variant={pageNum === currentPage ? "default" : "outline"}
                            size="sm"
                            onClick={() => setCurrentPage(pageNum)}
                            className="w-10"
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      다음
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                    >
                      마지막
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SurveyAnalytics;
